import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { ObservationsService } from '../../../services/Observations/observations.service';
import { firstValueFrom, Observable, of } from 'rxjs';

@Component({
  selector: 'app-observation-hashtags',
  templateUrl: './observation-hashtags.component.html',
  styleUrls: ['./observation-hashtags.component.scss'],
})
export class ObservationHashtagsComponent implements OnInit, OnChanges {
  @ViewChild('observationHashtagTrigger') autoTrigger: MatAutocompleteTrigger;

  hashtagsControl = new FormControl<string | null>('');
  hashtags: string[] = [];
  @Input() selectedHashtagsArray: string[] = [];
  @Input() projectId?: string;
  @Input() isOptional = true;
  loadingHashtags = false;
  filteredHashtags: Observable<string[]>;
  @Output() hashtagsEmitter = new EventEmitter<string[]>();

  /** After adding a tag, block auto-open until user focuses/clicks the input again (avoids panel reopening). */
  private autocompleteOpenSuppressed = false;
  /** True once `projectId` was applied via `ngOnChanges` (avoids duplicate fetch vs `ngOnInit`). */
  private projectIdAppliedFromChanges = false;

  constructor(
    private _transloco: TranslocoService,
    private _toaster: ToastrService,
    private _observationsService: ObservationsService,
  ) {}

  ngOnInit(): void {
    this.initializeHashtagsObservable();
    if (this.projectId && !this.projectIdAppliedFromChanges) {
      void this.loadHashtags().then(() => this.initializeHashtagsObservable());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const pid = changes['projectId'];
    if (pid && pid.currentValue && pid.currentValue !== pid.previousValue) {
      this.projectIdAppliedFromChanges = true;
      void this.loadHashtags().then(() => this.initializeHashtagsObservable());
    }
    const sel = changes['selectedHashtagsArray'];
    if (
      sel &&
      !sel.firstChange &&
      Array.isArray(sel.previousValue) &&
      sel.previousValue.length > 0 &&
      Array.isArray(sel.currentValue) &&
      sel.currentValue.length === 0
    ) {
      void this.loadHashtags().then(() => this.initializeHashtagsObservable());
    }
  }

  initializeHashtagsObservable(): void {
    // startWith: valueChanges does not emit until the user types; without it the async pipe has no
    // options on first focus, so openPanel() cannot show suggestions until after a keystroke.
    this.filteredHashtags = this.hashtagsControl.valueChanges.pipe(
      startWith(this.hashtagsControl.value ?? ''),
      distinctUntilChanged(),
      switchMap((hashtag) => {
        const v = hashtag ?? '';
        if (!v.trim()) {
          return of(this._availableForAutocomplete());
        }
        return of(this._filterHashtags(v));
      }),
    );
  }

  /** Compare tags ignoring case and optional leading # (API/chips may omit # while input adds it). */
  private _hashtagKey(s: string): string {
    return (s || '').trim().toLowerCase().replace(/^#/, '');
  }

  private _availableForAutocomplete(): string[] {
    return this.hashtags.filter(
      (h) => !this.selectedHashtagsArray.some((s) => this._hashtagKey(s) === this._hashtagKey(h)),
    );
  }

  /** User focused or clicked the input — allow the panel again after an add suppressed it. */
  onHashtagInputActivate(): void {
    this.autocompleteOpenSuppressed = false;
    this.scheduleOpenAutocompletePanel();
  }

  private scheduleOpenAutocompletePanel(): void {
    if (this.loadingHashtags || this.autocompleteOpenSuppressed) {
      return;
    }
    setTimeout(() => this.autoTrigger?.openPanel());
  }

  private closeAutocompletePanel(): void {
    this.autoTrigger?.closePanel();
    setTimeout(() => this.autoTrigger?.closePanel());
  }

  addHashtag(hashtag: string | null | undefined): void {
    const raw = (hashtag ?? this.hashtagsControl.value ?? '').toString().trim();
    if (!raw) {
      return;
    }

    const formattedHashtag = raw.startsWith('#') ? raw : '#' + raw;
    const incomingKey = this._hashtagKey(formattedHashtag);

    const duplicate = this.selectedHashtagsArray.some((h) => this._hashtagKey(h) === incomingKey);
    if (duplicate) {
      this._toaster.warning(this._transloco.translate('This hashtag already exists'));
      return;
    }

    this.autocompleteOpenSuppressed = true;
    this.closeAutocompletePanel();

    this.selectedHashtagsArray.push(formattedHashtag);
    this.hashtags = this.hashtags.filter((item) => this._hashtagKey(item) !== incomingKey);
    this.hashtagsControl.setValue('', { emitEvent: true });
    this.hashtagsEmitter.emit([...this.selectedHashtagsArray]);
    // Re-bind valueChanges → filtered list (no API call: list is already updated above).
    this.initializeHashtagsObservable();
    this.closeAutocompletePanel();
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.addHashtag(event.option.value as string);
  }

  onAddIconClick(event: MouseEvent): void {
    event.stopPropagation();
    this.addHashtag(this.hashtagsControl.value?.toString());
  }

  removeHashtag(hashtag: string): void {
    const key = this._hashtagKey(hashtag);
    this.selectedHashtagsArray = this.selectedHashtagsArray.filter(
      (h) => this._hashtagKey(h) !== key,
    );
    if (!this.hashtags.some((h) => this._hashtagKey(h) === key)) {
      this.hashtags.push(hashtag);
    }
    this.hashtagsControl.setValue(this.hashtagsControl.value ?? '', { emitEvent: true });
    this.hashtagsEmitter.emit([...this.selectedHashtagsArray]);
    this.initializeHashtagsObservable();
  }

  /**
   * Blocks parent form implicit submit on Enter. Always commits the current input text
   * (panel open or not). Keyboard selection on a highlighted option uses onOptionSelected.
   */
  onEnterKeydown(event: KeyboardEvent): void {
    if (!this.isOptional) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.addHashtag(this.hashtagsControl.value?.toString());
  }

  private _filterHashtags(value: string): string[] {
    const filterValue = this._hashtagKey(value);
    return this._availableForAutocomplete().filter((hashtag) =>
      this._hashtagKey(hashtag).includes(filterValue),
    );
  }

  /** True if value is already selected or matches a suggestion (for add-icon visibility). */
  hasHashtag(hashtag: string): boolean {
    const key = this._hashtagKey(hashtag);
    if (!key) {
      return false;
    }
    if (this.selectedHashtagsArray.some((s) => this._hashtagKey(s) === key)) {
      return true;
    }
    return this.hashtags.some((h) => this._hashtagKey(h) === key);
  }

  private async loadHashtags(): Promise<void> {
    if (!this.projectId) {
      return;
    }
    this.loadingHashtags = true;
    try {
      const res = await firstValueFrom(
        this._observationsService.getObservationHashtagsAll(this.projectId),
      );
      const parsed = this._parseHashtagResponse(res);
      const orderedUnique = this._dedupeHashtagsPreserveOrder(parsed);
      this.hashtags = orderedUnique.filter(
        (h) => !this.selectedHashtagsArray.some((s) => this._hashtagKey(s) === this._hashtagKey(h)),
      );
    } catch {
      this._toaster.warning(
        this._transloco.translate('Something went wrong, please try again later.'),
      );
    }
    this.loadingHashtags = false;
  }

  /** Preserve API order (e.g. newest first); dedupe case-insensitively. */
  private _dedupeHashtagsPreserveOrder(tags: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of tags) {
      const n = this._normalizeTag(t);
      if (!n) {
        continue;
      }
      const k = this._hashtagKey(n);
      if (!k || seen.has(k)) {
        continue;
      }
      seen.add(k);
      out.push(n);
    }
    return out;
  }

  private _normalizeTag(tag: string): string {
    const t = (tag || '').trim();
    if (!t) {
      return '';
    }
    return t.startsWith('#') ? t : '#' + t;
  }

  private _parseHashtagResponse(res: any): string[] {
    const raw = res?.data ?? res?.hashtags ?? res?.tags ?? res;
    if (!raw) {
      return [];
    }
    const list = Array.isArray(raw) ? raw : [];
    const out: string[] = [];
    for (const item of list) {
      if (typeof item === 'string') {
        out.push(item);
      } else if (item && typeof item === 'object') {
        const h = item.hashtag ?? item.name ?? item.tag ?? item.value ?? item.tag_details?.hashtag;
        if (h) {
          out.push(String(h));
        }
      }
    }
    return out;
  }
}
